import { type FC, useEffect, useState } from "react";
import Modal from "~/common/components/Modal";
import Spinner from "~/common/components/Spinner/Spinner";
import { Button, Stack } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";

interface manageModal {
  closeModal: () => void;
  addNewCard: () => void;
  updateCard: (data: CreditCard | undefined) => void;
  defaultCC?: CreditCard;
}

export interface CreditCard {
  cardHolderName?: string;
  cardName?: string;
  defaultCard: boolean;
  expMonth: string;
  expYear: string;
  expired: boolean;
  number: string;
  type: string;
  uid?: string;
}

export interface CreditCardListResponseDTO {
  cards?: CreditCard[];
}

const ManageCreditCard: FC<manageModal> = (props) => {
  const { t } = useTranslation();

  const { closeModal, addNewCard, updateCard, defaultCC } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [cards, setCards] = useState<Array<CreditCard>>([]);
  const [selectedCard, setSelectedCard] = useState<CreditCard>();

  const deleteCard = (id: string | undefined) => {
    occFetch(occUrl(`/checkout/multi/summary/delete-card?uid=${id}`), { method: "POST" })
      .then((res) => res.json())
      .then((json) => {
        setIsLoading(false);
        if (json === true) {
          const newArr = cards.filter((el: CreditCard) => el.uid !== id);
          if (defaultCC && defaultCC.uid === id) {
            updateCard(newArr[0] ? newArr[0] : undefined);
          }
          setCards(newArr);
        }
      })
      .catch(function () {
        setIsLoading(false);
      });
  };

  const changeDefaultCard = (name: string | undefined, id: string | undefined) => {
    occFetch(occUrl(`/checkout/multi/summary/update-cc?uid=${id}&name=${name}&defaultCard=true`), { method: "POST" })
      .then((res) => res.json())
      .then((json) => {
        if (json) {
          const newArr = cards.map((el: CreditCard) =>
            el.uid === id
              ? {
                  ...el,
                  defaultCard: true,
                }
              : {
                  ...el,
                  defaultCard: false,
                },
          );
          setCards(newArr);

          const selected = newArr.find((el: CreditCard) => el.defaultCard === true);
          setSelectedCard(selected);
          updateCard(selected);
        }
        setIsLoading(false);
      })
      .catch(function () {
        setIsLoading(false);
      });
  };

  const applyCardPayment = () => {
    if (selectedCard) {
      updateCard(selectedCard);
      closeModal();
    }
  };

  useEffect(() => {
    setIsLoading(true);
    occFetch(occUrl("/checkout/multi/summary/view-cards"))
      .then((res) => res.json())
      .then((json: CreditCardListResponseDTO) => {
        setCards(json.cards ?? []);
        setIsLoading(false);
      })
      .catch(function () {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (defaultCC) {
      setSelectedCard(defaultCC);
    }
  }, [defaultCC]);

  return (
    <Modal
      onClose={closeModal}
      title={t("checkoutPage.manageCreditCard.title")}
      footerChildren={
        <div className="d-flex flex-justify-between flex-align-center w-100">
          <Button onClick={() => addNewCard()} variant={"text"} data-test-id={"add-to-cart-btn-ok"}>
            <AddIcon className={"mr-1"} />
            {t("checkoutPage.addCreditCard.addNew")}
          </Button>
          <Stack direction={"row"} gap={1}>
            <Button onClick={() => closeModal()} variant={"text"} data-test-id={"add-to-cart-btn-ok"}>
              {t("checkoutPage.addCreditCard.btnCancel")}
            </Button>
            <Button onClick={applyCardPayment} data-test-id={"add-to-cart-btn-ok"} disabled={cards.length === 0}>
              {t("checkoutPage.addCreditCard.btnApply")}
            </Button>
          </Stack>
        </div>
      }
    >
      {isLoading && <Spinner />}
      {!isLoading && cards?.length > 0 && (
        <div className={"d-flex flex-direction-column"}>
          {cards.map((card: CreditCard, index: number) => (
            <>
              <div
                key={String(index)}
                className={
                  "mb-3 scha__order-summary-manage-credit-card-item" +
                  (card.uid === selectedCard?.uid && card.expired === false ? "__active" : "") +
                  (card.expired === true ? "__error" : "")
                }
                onClick={() => {
                  if (selectedCard?.uid !== card.uid && card.expired === false) {
                    setSelectedCard(card);
                  }
                }}
              >
                {card.uid === selectedCard?.uid && selectedCard?.expired !== true && (
                  <CheckIcon className={"scha__order-summary-manage-credit-card-item__icon"} />
                )}
                <h2 className="title-large f-bold mt-0 mb-3">{card.cardName}</h2>
                <div className={"d-flex flex-justify-between mb-3"}>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.nameCard")}
                    {card.cardHolderName && <p>{card.cardHolderName}</p>}
                  </div>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.ccnumber")}
                    {card.number && <p>**** **** **** {card.number}</p>}
                  </div>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.expDate")}
                    {card.expMonth && card.expYear && (
                      <p>
                        {card.expMonth}/{card.expYear}
                      </p>
                    )}
                  </div>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.cardType")}
                    {card.type && <p className={"text-uppercase"}>{card.type}</p>}
                  </div>
                </div>
                <div className={"d-flex"}>
                  <div
                    className={
                      "scha__order-summary-manage-credit-card-item__btn-options" +
                      (card.expired || card.defaultCard ? "-disabled" : "")
                    }
                    onClick={() => card.expired === false && changeDefaultCard(card?.cardName, card.uid)}
                    data-test-id={""}
                  >
                    {card.defaultCard ? (
                      <>{t("checkoutPage.manageCreditCard.default")}</>
                    ) : (
                      <>{t("checkoutPage.manageCreditCard.setDefault")}</>
                    )}
                  </div>
                  <div
                    className={"scha__order-summary-manage-credit-card-item__btn-options"}
                    onClick={() => deleteCard(card.uid)}
                    data-test-id={""}
                  >
                    {t("checkoutPage.manageCreditCard.delete")}
                  </div>
                </div>
              </div>
              {card.expired === true && (
                <div className={"mb-2"}>
                  <Alert severity={"error"}>{t("checkoutPage.manageCreditCard.expired")}</Alert>
                </div>
              )}
            </>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ManageCreditCard;
